import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CloseCircleOutline,
  CheckOutline,
  CustomAlert,
  useToast,
} from '@aslanpay/portal-common-lib';

import useGrantAccess from 'app/hooks/useGrantAccess';

import { GrantAccessModalProps } from './types';

function GrantAccessModal({
  employeeId,
  employeeName,
  isOpen,
  onClose,
  onGrant,
}: GrantAccessModalProps) {
  const toast = useToast();

  const { mutate: grantAccess, isLoading: isGrantingAccess } = useGrantAccess({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Access granted',
        description: `Access has been successfully granted to ${employeeName}. Their status is now ACTIVE.`,
      });

      onGrant();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, access could not be granted. Please try again.',
      });
    },
  });

  const handleGrant = () => {
    grantAccess({ id: employeeId });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Grant Access</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close grant access modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody mt="20px">
          <Stack spacing="20px">
            <CustomAlert
              variant="simple"
              status="info"
              title={
                <>
                  <Text textStyle="extraSmallMedium">Are you sure?</Text>
                  <Text textStyle="extraSmallRegular">
                    This will grant access to <strong>{employeeName}</strong> and reverse all LPS
                    actions while keeping the leave date intact. Their status will change to ACTIVE.
                  </Text>
                </>
              }
            />
            <Text textStyle="smallRegular" color="brand.darkGray">
              This action will:
            </Text>
            <Stack spacing="8px" pl="16px">
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Unblock the card and restore card access
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Re-enable all modules and features
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Change status from RESTRICTED to ACTIVE
              </Text>
              <Text textStyle="smallRegular" color="brand.darkGray">
                • Keep the leave date intact
              </Text>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose} mr="12px">
            Cancel
          </Button>
          <Button
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isGrantingAccess}
            onClick={handleGrant}
          >
            Confirm Grant
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default GrantAccessModal;
